import { DatePipe, NgClass } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, QueryList, ViewChildren } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faEraser, faFilter, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { FilterHandler } from 'src/app/core/filter-handler';
import { PropertyType } from 'src/app/models/enums/property-type';
import { IFilterable } from 'src/app/models/interfaces/filterable.interface';
import { ColumnDefinition } from '../advanced-gridview/models/column-definition';
import { ColumnListDefinition } from '../advanced-gridview/models/column-list-definition';
import { FilterDirective } from '../directives/filter.directive';
import { FormFocusDirective } from '../directives/form-focus.directive';
import { ListItem } from '../filters/models/list-item';
import { AvmDateAndTimePipe } from '../pipes/avm-date-and-time.pipe';
import { AvmDatePipe } from '../pipes/avm-date.pipe';
import { AvmFilterSubstringPipe } from '../pipes/avm-filter-substring.pipe';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-filters-bar',
    templateUrl: './filters-bar.component.html',
    styleUrls: ['./filters-bar.component.scss'],
    imports: [
        FormsModule,
        FormFocusDirective,
        ReactiveFormsModule,
        MatFormFieldModule,
        FaIconComponent,
        MatInputModule,
        MatButtonModule,
        MatTooltipModule,
        MatMenuModule,
        MatCheckboxModule,
        MatChipsModule,
        MatIconModule,
        NgClass,
        MatDatepickerModule,
        MatListModule,
        AvmDatePipe,
        AvmDateAndTimePipe,
        AvmFilterSubstringPipe,
    ]
})
export class FiltersBarComponent extends FilterDirective implements AfterViewInit {
    public resetFilterIcon = faEraser;
    public filterIcon = faFilter;

    /** Déclaration des ENUM */
    public Type = PropertyType;

    /**
     * Eléments affichés dans la liste déroulante dans le cas d'un attribut de type liste.
     */
    public listItems: ListItem<string>[];
    public faMagnifyingGlass = faMagnifyingGlass;

    /** Formulaire de recherche. */
    filterForm: UntypedFormGroup;

    _columnList: ColumnDefinition[] = [];

    @Input()
    set columnList(value: ColumnDefinition[]) {
        if (value) {
            this.loadFilters(value);
            this.initFilters();
        }
    }

    get columnList(): ColumnDefinition[] {
        return this._columnList;
    }

    @Input()
    filterableComponent: IFilterable;

    selectedColumns: ColumnDefinition[] = [];

    _filterHandler: FilterHandler;

    @Input()
    set filterHandler(value: FilterHandler) {
        if (value) {
            this._filterHandler = value;
            this.initFilters();
        }
    }

    get filterHandler(): FilterHandler {
        return this._filterHandler;
    }

    filtercounter: number = 0;

    /**
     * Liste des puces de filtre
     */
    @ViewChildren('filterChip') filterChips: QueryList<ElementRef> = new QueryList<ElementRef>();

    constructor(
        private formBuilder: UntypedFormBuilder,
        public override datePipe: DatePipe,
        private cdr: ChangeDetectorRef
    ) {
        super(datePipe);
        this.filterForm = this.formBuilder.group({
            searchControl: [''],
        });
    }

    ngAfterViewInit(): void {
        // Abonnement au changement de la liste des puces de filtre
        this.filterChips.changes.subscribe(() => {
            if (this.filterChips.length > 0 && this.filterChips.length > this.filtercounter) {
                this.filtercounter++;
                // Click sur la puce de filtre dernièrement créée
                this.filterChips.last.nativeElement.click();
            }
        });
    }

    /**
     * Indique si un contrôle retourne l'erreur de validation spécifiée.
     * @param controlName: Nom du contrôle.
     * @param errorName: Nom de l'erreur de validation
     * @returns : true si le contrôle retourne l'erreur de validation.
     */
    public hasError(controlName: string, errorName: string): boolean {
        return this.filterForm.controls[controlName].hasError(errorName);
    }

    /**
     * Emis lorsque l'utilisateur clique sur le bouton 'Rechercher'.
     * @param filterForm : formulaire de recherche.
     */
    public onSearch() {
        if (this.filterForm.valid) {
            this.filterHandler.search(this.filterForm.get('searchControl').value);
        }
    }

    /**
     * Reset du filtre.
     */
    public onUndo() {
        this.filterForm.reset();

        this.deleteAllFilters();
    }

    /**
     * Lors de la sélection d'un attribut à filtrer
     * @param attribute attribut à filtrer
     */
    public onUnfilteredColumnChage(attribute: ColumnDefinition) {
        this.deleteFilter(attribute);
    }

    /**
     * Lors de la sélection d'une colonne à ne plus filtrer
     * @param filteredColumn colonne filtré
     */
    public onFilteredColumnChange(filteredColumn: ColumnDefinition) {
        // Supprime de la liste des attributs l'attribut sélectionné
        this._columnList.splice(
            this._columnList.findIndex((a) => a.columnName === filteredColumn.columnName),
            1
        );
        this._columnList.sort((a, b) => a.headerText.localeCompare(b.headerText));

        // Ajoute l'attribut sélectionné dans la liste des attributs sélectionnés
        this.selectedColumns.push(filteredColumn);
    }

    /**
     * Lors de la suppression du filtre d'attribut
     * @param filter attribut filtré
     */
    public removeFilter(filter: ColumnDefinition) {
        this.deleteFilter(filter);
    }

    private loadFilters(definitions: ColumnDefinition[]) {
        this.selectedColumns = [];
        this.cdr.detectChanges();

        let columnDefinitions = Object.assign([], definitions);
        columnDefinitions = columnDefinitions.sort((a, b) => a.headerText.localeCompare(b.headerText));
        this._columnList = columnDefinitions;
    }

    private initFilters() {
        if (this._filterHandler && this._columnList) {
            this._filterHandler.initializeAttributeFilters(Object.assign([], this._columnList));
        }
    }

    private deleteAllFilters() {
        // Supprime les attributs sélectionnés
        this.selectedColumns.forEach((x) => {
            // Ajoute l'attribut sélectionné dans la liste des attributs
            this._columnList.push(x);
        });

        // Reset le filtre
        this._filterHandler.resetAllAttributesFilters();

        this.selectedColumns = [];
        this._columnList.sort((a, b) => a.headerText.localeCompare(b.headerText));

        // Met à jour le compteur de filtre
        this.filtercounter = 0;
    }

    private deleteFilter(attribute: ColumnDefinition) {
        // Supprime de la liste des attributs sélectionnés l'attribut sélectionné
        this.selectedColumns.splice(
            this.selectedColumns.findIndex((a) => a.columnName === attribute.columnName),
            1
        );

        // Ajoute l'attribut sélectionné dans la liste des attributs
        this._columnList.push(attribute);
        this._columnList.sort((a, b) => a.headerText.localeCompare(b.headerText));

        // Reset le filtre
        this._filterHandler.resetAttributeFilter(attribute.columnName);

        // Met à jour le compteur de filtre
        this.filtercounter--;
    }

    public CancelAttributFilter(filterName: string) {
        this._filterHandler.resetAttributeFilter(filterName);
    }

    public getValues(columnDefinition: ColumnListDefinition<string>): ListItem<string>[] {
        let listItems: ListItem<string>[] = [];

        if (columnDefinition.dataSource) {
            for (let key of columnDefinition.dataSource.keys()) {
                listItems.push(columnDefinition.dataSource.get(key));
            }
        }

        return listItems;
    }

    public getValues2(columnDefinition: ColumnDefinition): ListItem<string>[] {
        return this.getValues(columnDefinition as unknown as ColumnListDefinition<string>);
    }
}
